/**
 * PassScanPage — V2 Event Ticket QR Scanner (Phase 11c)
 *
 * Route: /admin/scan-pass
 * Role:  super_admin | manager | reception
 *
 * Flow:
 *   1. Camera-based QR scan via html5-qrcode (env-facing back camera by default)
 *   2. QR encodes a server-signed ticket_token JWT (~200 chars)
 *   3. POST /api/v2/admin/bookings/scan { token }
 *   4. Render result: customer, event, passes, booking_code, check-in stamp
 *   5. 1.5s cooldown before next scan to prevent double-scan / repeated reads
 *
 * Fallback: manual paste of the ticket_token (e.g. when camera permission denied
 *           or scanning a printed token on a low-end device).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { toast } from 'sonner';
import {
  Scan, Camera, CameraOff, RotateCcw, Check, AlertCircle, Loader2,
  Ticket, User, MapPin, Clock as ClockIcon, Keyboard,
} from 'lucide-react';

import AdminShell from '../../components/admin/AdminShell';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { adminV2Api } from '../../lib/api';
import { cn, formatDate } from '../../lib/utils';

const SCANNER_ID    = 'v2-pass-scan-frame';
const SCAN_COOLDOWN = 1500;   // ms — block re-decode of same QR while it's still in frame

function classifyCameraError(err) {
  const name = err?.name || '';
  const msg  = (err?.message || '').toLowerCase();
  if (name === 'NotAllowedError' || msg.includes('permission'))
    return { kind: 'denied',     title: 'Camera permission denied',
             hint: 'Allow camera access in your browser settings and refresh.' };
  if (name === 'NotFoundError'   || msg.includes('not found'))
    return { kind: 'no_device',  title: 'No camera found',
             hint: 'Connect a camera or use manual entry.' };
  if (name === 'NotReadableError' || msg.includes('in use'))
    return { kind: 'in_use',     title: 'Camera busy',
             hint: 'Another tab or app is using your camera. Close it and try again.' };
  return { kind: 'generic', title: 'Could not start camera', hint: err?.message || 'Unknown error' };
}

function ResultStatusPill({ result }) {
  if (!result) return null;
  if (result.already_checked_in) {
    return (
      <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="scan-result-status-warn">
        Already Checked In
      </Badge>
    );
  }
  if (result.ok) {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="scan-result-status-ok">
        Check-in Successful
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-500/15 text-red-300 border border-red-500/30" data-testid="scan-result-status-err">
      Failed
    </Badge>
  );
}

function ResultCard({ result, error, onScanNext }) {
  if (error) {
    return (
      <div className="surface-card rounded-xl p-6 border border-red-500/40" data-testid="scan-error-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
            <AlertCircle size={18} className="text-red-400" />
          </div>
          <div className="flex-1">
            <p className="font-['EB_Garamond'] text-xl text-[#F2F2F2]">{error.title || 'Scan failed'}</p>
            <p className="text-sm text-[#B9B9B9] mt-1">{error.detail}</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={onScanNext}
          data-testid="scan-next-btn"
        >
          <Scan size={14} className="mr-2" /> Scan another
        </Button>
      </div>
    );
  }

  if (!result?.booking) return null;
  const b = result.booking;
  const isAlready = result.already_checked_in;

  return (
    <div
      className={cn(
        'surface-card rounded-xl p-6 transition-colors',
        isAlready ? 'border border-amber-500/40' : 'border border-emerald-500/40',
      )}
      data-testid="scan-result-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center',
            isAlready
              ? 'bg-amber-500/10 border border-amber-500/30'
              : 'bg-emerald-500/10 border border-emerald-500/30',
          )}>
            <Check size={18} className={isAlready ? 'text-amber-300' : 'text-emerald-300'} />
          </div>
          <div>
            <p className="font-['EB_Garamond'] text-xl text-[#F2F2F2] leading-tight">
              {isAlready ? 'Already Checked In' : 'Welcome In ✨'}
            </p>
            <p className="text-xs text-[#7A7A7A] mt-0.5">
              {isAlready && b.checked_in_at
                ? `First scanned at ${new Date(b.checked_in_at).toLocaleString('en-IN', { timeStyle: 'short', dateStyle: 'medium', timeZone: 'Asia/Kolkata' })}`
                : 'Check-in recorded just now'}
            </p>
          </div>
        </div>
        <ResultStatusPill result={result} />
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-2 text-[#F2F2F2]">
          <User size={14} className="text-[#D4A843]" />
          <span data-testid="scan-result-customer">{b.customer_name || 'Guest'}</span>
          {b.customer_phone && (
            <span className="text-xs text-[#7A7A7A] ml-2 tabular-nums">{b.customer_phone}</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[#F2F2F2]">
          <Ticket size={14} className="text-[#D4A843]" />
          <span className="font-semibold">{b.event_title}</span>
          <Badge className="bg-[#D4A843]/15 text-[#D4A843] border border-[#D4A843]/30 ml-1 tabular-nums" data-testid="scan-result-bcode">
            {b.booking_code}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-[#B9B9B9]">
          <ClockIcon size={14} className="text-[#7A7A7A]" />
          <span>{formatDate(b.event_date)}{b.event_time ? ` · ${b.event_time}` : ''}</span>
          {b.venue && (
            <>
              <MapPin size={14} className="text-[#7A7A7A] ml-2" />
              <span>{b.venue}</span>
            </>
          )}
        </div>
      </div>

      {/* Passes summary */}
      {Array.isArray(b.items) && b.items.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#202020]">
          <p className="text-xs text-[#B9B9B9] uppercase tracking-wider font-medium mb-2">Passes</p>
          <ul className="space-y-1.5">
            {b.items.map((it, idx) => (
              <li key={idx} className="flex items-center justify-between text-sm">
                <span className="text-[#F2F2F2]">{it.pass_type_name || it.name || '—'}</span>
                <span className="text-[#B9B9B9] tabular-nums">× {it.quantity}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button
        className="w-full mt-5 bg-[#D4A843] text-[#0A0A0A] hover:bg-[#C49A35]"
        onClick={onScanNext}
        data-testid="scan-next-btn"
      >
        <Scan size={14} className="mr-2" /> Scan Next Guest
      </Button>
    </div>
  );
}

export default function PassScanPage() {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);    // { ok, booking, already_checked_in }
  const [error, setError] = useState(null);
  const cooldownRef = useRef(0);

  // ── Submit to backend ────────────────────────────────────────────────────
  const submitToken = useCallback(async (token) => {
    if (!token || submitting) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminV2Api.scanTicket(token.trim());
      setResult(res?.data || {});
      const data = res?.data || {};
      if (data.already_checked_in) {
        toast.warning(`Already checked in at ${new Date(data.booking?.checked_in_at).toLocaleTimeString('en-IN')}`);
      } else if (data.ok) {
        toast.success(`✓ ${data.booking?.customer_name || 'Guest'} · ${data.booking?.booking_code}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || err?.message || 'Scan failed');
      setError({
        title:  err?.response?.status === 404 ? 'Booking not found' : 'Invalid ticket',
        detail: msg,
      });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [submitting]);

  // ── Decode handler with cooldown to prevent rapid re-reads ──────────────
  const handleDecoded = useCallback((text) => {
    const now = Date.now();
    if (now - cooldownRef.current < SCAN_COOLDOWN) return;
    cooldownRef.current = now;
    submitToken(text);
  }, [submitToken]);

  // ── Start camera ─────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError({
        kind:  'unsupported',
        title: 'Browser does not expose camera APIs',
        hint:  'Open this page in Chrome / Safari over HTTPS, or use manual entry.',
      });
      setManualMode(true);
      return;
    }
    try {
      const qr = new Html5Qrcode(SCANNER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = qr;
      await qr.start(
        { facingMode: { ideal: 'environment' } },
        {
          fps: 12,
          qrbox: (vw, vh) => {
            const minDim = Math.min(vw, vh);
            const size = Math.max(220, Math.floor(minDim * 0.75));
            return { width: size, height: size };
          },
          aspectRatio: 1.0,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          videoConstraints: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        (decodedText) => handleDecoded(decodedText),
        () => {/* per-frame failures are noisy — ignore */},
      );
      setScanning(true);
    } catch (err) {
      const info = classifyCameraError(err);
      setCameraError(info);
      setScanning(false);
    }
  }, [handleDecoded]);

  const stopCamera = useCallback(async () => {
    const qr = scannerRef.current;
    if (!qr) return;
    try {
      await qr.stop();
      await qr.clear();
    } catch { /* already stopped */ }
    scannerRef.current = null;
    setScanning(false);
  }, []);

  // Auto-start on mount; cleanup on unmount
  useEffect(() => {
    startCamera();
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Scan next: reset result + restart camera ─────────────────────────────
  const handleScanNext = async () => {
    setResult(null);
    setError(null);
    setManualToken('');
    cooldownRef.current = 0;
    if (!scanning) await startCamera();
  };

  // ── Manual submit ────────────────────────────────────────────────────────
  const handleManualSubmit = async (e) => {
    e?.preventDefault?.();
    if (!manualToken.trim()) return;
    await stopCamera();
    submitToken(manualToken);
  };

  return (
    <AdminShell title="Scan Pass">
      <div className="space-y-4 max-w-3xl mx-auto" data-testid="pass-scan-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] leading-tight">
              Scan Pass
            </h1>
            <p className="text-sm text-[#B9B9B9] mt-1">
              Point your camera at the guest&apos;s ticket QR. Double-scans are blocked automatically.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setManualMode((v) => !v)}
            data-testid="toggle-manual-mode"
          >
            <Keyboard size={14} className="mr-2" />
            {manualMode ? 'Use Camera' : 'Manual Entry'}
          </Button>
        </div>

        {/* Camera surface */}
        {!manualMode && (
          <div className="surface-card rounded-xl overflow-hidden" data-testid="pass-scan-camera">
            <div className="relative bg-[#0A0A0A]">
              <div
                id={SCANNER_ID}
                className="w-full aspect-square max-h-[70vh] mx-auto"
                style={{ minHeight: 240 }}
              />
              {/* Loading overlay before camera spins up */}
              {!scanning && !cameraError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0A]/80">
                  <Loader2 className="animate-spin text-[#D4A843]" size={28} />
                  <p className="text-sm text-[#B9B9B9] mt-3">Starting camera…</p>
                </div>
              )}
              {/* Submitting overlay */}
              {submitting && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0A]/85"
                  data-testid="scan-submitting"
                >
                  <Loader2 className="animate-spin text-[#D4A843]" size={28} />
                  <p className="text-sm text-[#F2F2F2] mt-3">Verifying ticket…</p>
                </div>
              )}
            </div>

            {/* Camera error */}
            {cameraError && (
              <div className="p-5 border-t border-[#202020]" data-testid="pass-scan-camera-error">
                <div className="flex items-start gap-3">
                  <CameraOff size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[#F2F2F2] font-semibold">{cameraError.title}</p>
                    <p className="text-xs text-[#B9B9B9] mt-1">{cameraError.hint}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={startCamera}>
                    <RotateCcw size={14} className="mr-2" /> Retry
                  </Button>
                  <Button onClick={() => setManualMode(true)}>
                    <Keyboard size={14} className="mr-2" /> Enter manually
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manual entry */}
        {manualMode && (
          <form
            onSubmit={handleManualSubmit}
            className="surface-card rounded-xl p-5 space-y-3"
            data-testid="pass-scan-manual"
          >
            <label className="block">
              <span className="text-xs text-[#B9B9B9] uppercase tracking-wider font-medium">Ticket Token</span>
              <textarea
                className="w-full mt-1 bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg p-3 text-[#F2F2F2] focus:border-[#D4A843] focus:outline-none transition-colors text-xs tabular-nums"
                rows={4}
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                placeholder="Paste the ticket_token shown on the customer's pass…"
                data-testid="pass-scan-manual-input"
              />
            </label>
            <Button
              type="submit"
              disabled={submitting || !manualToken.trim()}
              className="w-full bg-[#D4A843] text-[#0A0A0A] hover:bg-[#C49A35]"
              data-testid="pass-scan-manual-submit"
            >
              {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Verifying…</> : <><Check size={14} className="mr-2" /> Verify Ticket</>}
            </Button>
          </form>
        )}

        {/* Result panel */}
        <ResultCard
          result={result}
          error={error}
          onScanNext={handleScanNext}
        />

        {/* Tip */}
        {!result && !error && !cameraError && (
          <div className="surface-card rounded-xl p-4 text-xs text-[#7A7A7A] flex items-start gap-3" data-testid="scan-tips">
            <Camera size={14} className="text-[#D4A843] mt-0.5 flex-shrink-0" />
            <p>
              Hold the phone roughly 15-20 cm from the QR. On dim phones, ask the guest to raise brightness.
              First-time scans require camera permission via your browser.
            </p>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
